"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BanditEvaluator = void 0;
const constants_1 = require("./constants");
const sharders_1 = require("./sharders");
class BanditEvaluator {
    constructor() {
        this.assignmentShards = constants_1.BANDIT_ASSIGNMENT_SHARDS; // We just hard code this for now
        this.sharder = new sharders_1.MD5Sharder();
    }
    evaluateBestBanditAction(subjectAttributes, actions, banditModel) {
        const actionScores = this.scoreActions(subjectAttributes, actions, banditModel);
        const { topAction } = this.getTopScore(actionScores);
        return topAction;
    }
    evaluateBandit(flagKey, subjectKey, subjectAttributes, actions, banditModel) {
        const actionScores = this.scoreActions(subjectAttributes, actions, banditModel);
        const actionWeights = this.weighActions(actionScores, banditModel.gamma, banditModel.actionProbabilityFloor);
        const selectedActionKey = this.selectAction(flagKey, subjectKey, actionWeights);
        // Compute optimality gap in terms of score
        const topScore = Object.values(actionScores).reduce((maxScore, score) => (score > maxScore ? score : maxScore), -Infinity);
        const optimalityGap = topScore - actionScores[selectedActionKey];
        return {
            flagKey,
            subjectKey,
            subjectAttributes,
            actionKey: selectedActionKey,
            actionAttributes: actions[selectedActionKey],
            actionScore: actionScores[selectedActionKey],
            actionWeight: actionWeights[selectedActionKey],
            gamma: banditModel.gamma,
            optimalityGap,
        };
    }
    scoreActions(subjectAttributes, actions, banditModel) {
        const actionScores = {};
        Object.entries(actions).forEach(([actionKey, actionAttributes]) => {
            let score = banditModel.defaultActionScore;
            const coefficients = banditModel.coefficients[actionKey];
            if (coefficients) {
                score = coefficients.intercept;
                score += this.scoreNumericAttributes(coefficients.subjectNumericCoefficients, subjectAttributes.numericAttributes);
                score += this.scoreCategoricalAttributes(coefficients.subjectCategoricalCoefficients, subjectAttributes.categoricalAttributes);
                score += this.scoreNumericAttributes(coefficients.actionNumericCoefficients, actionAttributes.numericAttributes);
                score += this.scoreCategoricalAttributes(coefficients.actionCategoricalCoefficients, actionAttributes.categoricalAttributes);
            }
            actionScores[actionKey] = score;
        });
        return actionScores;
    }
    scoreNumericAttributes(coefficients, attributes) {
        return coefficients.reduce((score, numericCoefficients) => {
            const attributeValue = attributes[numericCoefficients.attributeKey];
            if (typeof attributeValue === 'number' && isFinite(attributeValue)) {
                score += attributeValue * numericCoefficients.coefficient;
            }
            else {
                score += numericCoefficients.missingValueCoefficient;
            }
            return score;
        }, 0);
    }
    scoreCategoricalAttributes(coefficients, attributes) {
        return coefficients.reduce((score, attributeCoefficients) => {
            const attributeValue = attributes[attributeCoefficients.attributeKey]?.toString();
            const applicableCoefficient = attributeValue && attributeCoefficients.valueCoefficients[attributeValue];
            score +=
                typeof applicableCoefficient === 'number'
                    ? applicableCoefficient
                    : attributeCoefficients.missingValueCoefficient;
            return score;
        }, 0);
    }
    weighActions(actionScores, gamma, actionProbabilityFloor) {
        const actionWeights = {};
        const actionScoreEntries = Object.entries(actionScores);
        if (!actionScoreEntries.length) {
            return actionWeights;
        }
        const { topScore: currTopScore, topAction: currTopAction } = this.getTopScore(actionScores);
        if (currTopScore === null || currTopAction === null) {
            // Appease typescript with this check and extra variables
            throw new Error('Unable to find top score');
        }
        const topScore = currTopScore;
        const topAction = currTopAction;
        // Then weigh every action but the top one
        const numActions = actionScoreEntries.length;
        const minimumWeight = actionProbabilityFloor / numActions;
        let cumulativeWeight = 0;
        actionScoreEntries.forEach(([actionKey, actionScore]) => {
            if (actionKey === topAction) {
                // We weigh the top action later
                return;
            }
            const weight = 1 / (numActions + gamma * (topScore - actionScore));
            const boundedWeight = Math.max(weight, minimumWeight);
            cumulativeWeight += boundedWeight;
            actionWeights[actionKey] = boundedWeight;
        });
        // Finally weigh the top action (defensively bounding to 0.0)
        actionWeights[topAction] = Math.max(1 - cumulativeWeight, 0.0);
        return actionWeights;
    }
    selectAction(flagKey, subjectKey, actionWeights) {
        // Deterministically "shuffle" the actions
        // This way as action weights shift, a bunch of users who were on the edge of one action won't all be shifted to the
        // same new action at the same time.
        const shuffledActions = Object.entries(actionWeights).sort((a, b) => {
            const actionAShard = this.sharder.getShard(`${flagKey}-${subjectKey}-${a[0]}`, this.assignmentShards);
            const actionBShard = this.sharder.getShard(`${flagKey}-${subjectKey}-${b[0]}`, this.assignmentShards);
            let result = actionAShard - actionBShard;
            if (result === 0) {
                // In the unlikely case of a tie in randomized assigned shards, break the tie with the action names
                result = a[0] < b[0] ? -1 : 1;
            }
            return result;
        });
        // Select action from the shuffled actions, based on weight
        const assignedShard = this.sharder.getShard(`${flagKey}-${subjectKey}`, this.assignmentShards);
        const assignmentWeightThreshold = assignedShard / this.assignmentShards;
        let cumulativeWeight = 0;
        let assignedAction = null;
        for (const actionWeight of shuffledActions) {
            cumulativeWeight += actionWeight[1];
            if (cumulativeWeight > assignmentWeightThreshold) {
                assignedAction = actionWeight[0];
                break;
            }
        }
        if (assignedAction === null) {
            throw new Error(`No bandit action selected for flag "${flagKey}" and subject "${subjectKey}"`);
        }
        return assignedAction;
    }
    getTopScore(actionScores) {
        const actionScoreEntries = Object.entries(actionScores);
        // Find the action with the highest score, tie-breaking by name, selecting the alpha-numerically smaller key.
        let topScore = null;
        let topAction = null;
        actionScoreEntries.forEach(([actionKey, actionScore]) => {
            if (topScore === null ||
                topAction === null ||
                actionScore > topScore ||
                (actionScore === topScore && actionKey < topAction)) {
                topScore = actionScore;
                topAction = actionKey;
            }
        });
        return { topScore, topAction };
    }
}
exports.BanditEvaluator = BanditEvaluator;
//# sourceMappingURL=bandit-evaluator.js.map